import axios, { type AxiosResponse, type AxiosError } from 'axios';
import type { PublicDashboardResponse, DashboardWidget } from '../types/widget';
import type { QueryConfig, WidgetDataResponse } from '../types/analytics';



// Environment-based public API configuration (no auth required)
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;
const PUBLIC_ANALYTICS_API_BASE_URL = (import.meta.env.VITE_PUBLIC_ANALYTICS_API_URL || (import.meta.env.VITE_ANALYTICS_API_URL || import.meta.env.VITE_ANALYTICS_API_BASE_URL) + '/public').replace(/\/+$/, '');

// Create public API axios instance (no auth)
const publicApiClient = axios.create({
  baseURL: PUBLIC_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Create public analytics API axios instance (no auth, uses headers for context)
const publicAnalyticsApiClient = axios.create({
  baseURL: PUBLIC_ANALYTICS_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Response interceptor for public API error handling
publicApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ Public API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    const { response, config } = error;

    if (response) {
      console.error(`❌ Public API Error: ${response.status} ${config?.method?.toUpperCase()} ${config?.url}`);
    } else if (error.request) {
      console.error('❌ Public API Network Error: No response received');
    } else {
      console.error('❌ Public API Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Response interceptor for public analytics API error handling
publicAnalyticsApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`✅ Public Analytics API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    const { response, config } = error;

    if (response) {
      console.error(`❌ Public Analytics API Error: ${response.status} ${config?.method?.toUpperCase()} ${config?.url}`);
    } else if (error.request) {
      console.error('❌ Public Analytics API Network Error: No response received');
    } else {
      console.error('❌ Public Analytics API Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Convert camelCase keys to snake_case for API request
 */
const camelToSnake = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(camelToSnake);
  if (typeof obj !== 'object') return obj;

  const converted: Record<string, unknown> = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    converted[snakeKey] = camelToSnake(obj[key]);
  }
  return converted;
};

/**
 * Convert snake_case keys to camelCase for API response
 */
const snakeToCamel = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(snakeToCamel);
  if (typeof obj !== 'object') return obj;

  const converted: Record<string, unknown> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    converted[camelKey] = snakeToCamel(obj[key]);
  }
  return converted;
};

/**
 * Public Dashboard Service - for unauthenticated access to shared dashboards
 */
export const publicDashboardService = {
  /**
   * Get a shared dashboard via public token
   * @param token - The share token from the URL
   */
  async getSharedDashboard(token: string): Promise<PublicDashboardResponse> {
    const response = await publicApiClient.get(`/shared-dashboard/${token}`);
    return response.data?.data || response.data;
  },
};

/**
 * Public Analytics Service - for fetching widget data on public shared dashboards
 */
export const publicAnalyticsService = {
  /**
   * Fetch widget data for a public shared dashboard
   * @param queryConfig - The widget query configuration
   * @param ownerUserId - The dashboard owner's user ID
   * @param accountId - The account ID
   * @param shareToken - The share token for authentication
   */
  async fetchWidgetData<T = Record<string, unknown>>(
    queryConfig: QueryConfig,
    ownerUserId: string,
    accountId: string,
    shareToken: string
  ): Promise<WidgetDataResponse<T>> {
    const snakeCaseConfig = camelToSnake(queryConfig);

    const response = await publicAnalyticsApiClient.post(
      '/shared/widget-data?format=v2',
      { query_config: snakeCaseConfig },
      {
        headers: {
          'X-Share-Token': shareToken,
          'X-Owner-User-ID': String(ownerUserId),
          'X-Account-ID': String(accountId),
        },
      }
    );

    return snakeToCamel(response.data) as WidgetDataResponse<T>;
  },
};

export { publicApiClient, publicAnalyticsApiClient, PUBLIC_API_BASE_URL, PUBLIC_ANALYTICS_API_BASE_URL };